/*
  <bmi-check> — BMI eligibility checker for the GLP-1 landing page.

  Responsibilities:
  - Read height (ft + in) + weight (lbs) from the form
  - Compute BMI using the imperial formula: weight / (height_in^2) * 703
  - Render a result card with one of three eligibility tiers + an appropriate CTA
  - Fire a GTM event with the computed BMI and the result tier
  - Honor the user's selected pricing tier (from <pricing-tiers>) when constructing
    the result CTA's URL, so the experience is continuous across the page
*/

(function () {
  if (typeof window === 'undefined') return;
  if (customElements.get('bmi-check')) return;

  function pushDataLayer(eventObj) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventObj);
  }

  function appendPlanParam(baseUrl, tierId) {
    if (!tierId) return baseUrl;
    var separator = baseUrl.indexOf('?') >= 0 ? '&' : '?';
    return baseUrl + separator + 'plan=' + encodeURIComponent(tierId);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  customElements.define(
    'bmi-check',
    class BmiCheck extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-form]');
        this.result = this.querySelector('[data-result]');
        this.ctaUrlBase = this.getAttribute('data-cta-url') || 'https://intake.mintmed.com/#start';

        if (!this.form || !this.result) return;

        this._onSubmit = this._onSubmit.bind(this);
        this.form.addEventListener('submit', this._onSubmit);
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this._onSubmit);
        }
      }

      _onSubmit(e) {
        e.preventDefault();
        var data = new FormData(this.form);
        var ft = parseInt(data.get('height_ft'), 10);
        var inch = parseInt(data.get('height_in'), 10);
        var weight = parseFloat(data.get('weight'));

        if (isNaN(ft) || isNaN(inch) || isNaN(weight)) return;

        var heightIn = ft * 12 + inch;
        if (heightIn <= 0) return;
        if (weight < 50 || weight > 800) return;

        var bmi = (weight / (heightIn * heightIn)) * 703;
        this._renderResult(bmi);
      }

      _renderResult(bmi) {
        var bmiRounded = Math.round(bmi * 10) / 10;
        var tier, title, message, eligible;

        if (bmi >= 30) {
          tier = 'likely';
          title = "You're likely a candidate";
          message = "Patients with a BMI of 30+ commonly qualify for compounded GLP-1 treatment. A licensed U.S. physician will confirm during your free evaluation.";
          eligible = true;
        } else if (bmi >= 27) {
          tier = 'maybe';
          title = "You may qualify";
          message = "With a BMI of 27-30, qualification depends on related health factors. A licensed U.S. physician will confirm during your free evaluation.";
          eligible = true;
        } else {
          tier = 'unlikely';
          title = "GLP-1s may not be the right fit";
          message = "Compounded semaglutide is typically prescribed at BMI 27+. Your doctor can discuss alternative approaches that may fit you better.";
          eligible = false;
        }

        var selectedTier = window.__glp1CurrentTier || '';
        var ctaHref = eligible ? appendPlanParam(this.ctaUrlBase, selectedTier) : '';

        var ctaHtml = eligible
          ? '<a class="glp1-bmi-checker__result-cta button button--primary button--md" href="' + escapeHtml(ctaHref) + '" data-glp1-cta data-position="bmi-result" is="hover-link"><span class="btn-fill" data-fill></span><span class="btn-text">Start my free evaluation</span></a>'
          : '';

        this.result.innerHTML =
          '<div class="glp1-bmi-checker__result-card' + (eligible ? ' is-eligible' : '') + '">' +
            '<div class="glp1-bmi-checker__bmi">Your BMI: <strong>' + bmiRounded + '</strong></div>' +
            '<h3 class="glp1-bmi-checker__result-title">' + escapeHtml(title) + '</h3>' +
            '<p class="glp1-bmi-checker__result-message">' + escapeHtml(message) + '</p>' +
            ctaHtml +
          '</div>';

        this.result.removeAttribute('hidden');

        // Smooth-scroll the result into view if it's below the fold
        var rect = this.result.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        pushDataLayer({
          event: 'landing_page_eligibility_check',
          bmi: bmiRounded,
          result: tier
        });
      }
    }
  );
})();
